import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { Text } from '@contrast-security-inc/design-system-typography'
import { AgentWizardStepContainer } from './agent-wizard-step-container'
import { AgentWizardLanguageSelect } from './agent-wizard-language-select'
import { useApiKeys } from './api'
import { StepSelector } from './step-selector'
import {
  onboardViaDocsAgentWizardSteps,
  stepMappingNormalizeAgentDownload
} from './steps'
import {
  getOrgUuid,
  getCurrentLanguage,
  setCurrentLanguage,
  nullFn
} from './common/utils'
import { useApiCredentials } from '../../../../components/api-credentials/api/use-api-credentials'
import { useOrganizationSettingsQuery } from '../../../../components/organization-settings/use-organization-settings'
import { formatStyles } from '../../../..//components/react-intl-helpers'

/*
example code left for developers but commented to reduce uncessary bytes in our payload

const exampleMessage = `<text-titleH2>Wizard Markup Examples</text-titleH2>

<br></br>
<text-label><i>win/meta/command - b</i></text-label> : will insert a break (<hl>'<br'>'</br'></hl>) at the current position.

<br></br>
some examples are provided below, more info on <b>markup tags</b> available here <hl>/src/components/react-intl-helpers.tsx</hl>.

<br></br><br></br>
<cb>console.log('hello world');</cb>

<br></br>
<warn>Warning</warn>

<br></br>
<info>Information</info>`
*/

// private
// this keeps the interpreter from failing on template variables
const processValues = (msgContent) =>
  msgContent.replaceAll('{', '[').replaceAll('}', ']')
// a simple message editor to make adding new wizard steps easier
export const AgentWizardEditor = () => {
  // exported strictly for test
  const intl = useIntl()
  const textareaRef = React.createRef<HTMLTextAreaElement>()
  const [statusText, setStatusText] = React.useState('')
  const [message, setMessage] = React.useState('') // React.useState(exampleMessage)

  const insertBreak = React.useCallback(() => {
    if (textareaRef.current) {
      const pos = textareaRef.current.selectionStart
      setMessage(message.slice(0, pos) + '<br></br>' + message.slice(pos))
    }
  }, [textareaRef, message])

  return (
    <div
      data-testid="agent-wizard-editor"
      className={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <Text as="div" font="body" testId="agent-wizard-editor-output">
        {intl.formatMessage(
          {
            id: '...',
            defaultMessage: processValues(message)
          },
          {
            ...formatStyles.button(),
            ...formatStyles.doclink(),
            ...formatStyles.link(),
            ...formatStyles.table({
              columns: [{ accessorKey: 'name' }],
              data: [{ name: 'data' }]
            })
          }
        )}
      </Text>
      <div
        className={css`
          border-top: 1px #aaa solid;
          padding-top: 16px;
        `}
      >
        <textarea
          ref={textareaRef}
          data-testid="agent-wizard-editor-textarea"
          className={css`
            outline: none;
            width: 100%;
            border-radius: 5px;
            border: 1px #ddd solid;
            font-family: monospace;
            min-height: 200px;
            resize: vertical;
            background: #eee;
            padding: 8px;
          `}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={(event) =>
            event.metaKey && event.key === 'b' && insertBreak()
          }
          value={message}
        />
      </div>
      <div
        className={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: end;
          gap: 16px;
        `}
      >
        <span>{statusText}</span>
        <Button
          variant="secondary"
          onClick={() => {
            setMessage(message.replaceAll('<br></br>', '\n<br></br>\n'))
          }}
        >
          Format Markup
        </Button>
        <Button
          onClick={async () => {
            await navigator.clipboard.writeText(
              `"": "${message.replaceAll('\n', '')}"`
            )
            setStatusText('Copied your message to clipboard!')
            setTimeout(() => setStatusText(''), 5000)
          }}
        >
          Copy Markup
        </Button>
      </div>
    </div>
  )
}

export const StepContent = ({ step, isSecondary = false }) => {
  const intl = useIntl()

  // much prettier, so parentheses
  const contentToRender =
    typeof step.Content === 'function' ? (
      step.Content(step)
    ) : step.Content?.message ? (
      intl.formatMessage({ id: step.Content.message }, step.Content.values)
    ) : step.Content?.options?.length ? (
      <StepSelector
        size={step.Content.size}
        postDetailMsg={step.Content.postDetailMsg}
        detailMsg={step.Content.detailMsg}
        options={step.Content.options}
        branchKey={step.branchKey}
      />
    ) : (
      step.Content
    )

  if (step.secondContent && isSecondary) {
    return step.secondContent
  }

  return contentToRender
}
const AgentWizardStepList = ({ index, steps }) => {
  const ctx = React.useContext(WizardStepContext)

  return steps.map((step, i) => {
    const { branches, branchKey } = step
    const branchOption = ctx.branches[branchKey]

    return (
      // branchOption required to not re-render
      // different branch components with shared steps
      <React.Fragment key={`${i}:${step.titleMsg}:${branchOption}`}>
        <AgentWizardStepContainer
          // keep the step open if the user made a choice on this step
          step={
            !!branchOption && branches ? { ...step, defaultIsOpen: true } : step
          }
          number={index + i}
        >
          <StepContent step={step} />
        </AgentWizardStepContainer>
        {branches?.[branchOption] && (
          <AgentWizardStepList
            steps={branches[branchOption]}
            index={index + i + 1}
          />
        )}
      </React.Fragment>
    )
  })
}

// public
export interface WizardStepContextValueType {
  updateAgentUid: (agentUid: string) => void
  agentUid: string
  setAgentUid: (agentUid: string) => void
  appUuid: string
  setAppUuid: (appUuid: string) => void
  branches: Record<string, string>
  setBranch: (key: string, value: string) => void
  resetBranches: () => void
  apiCredentials: {
    service_key?: string
    user_uid?: string
    api_key?: string
    agentUrl?: string
    token?: string
    userCredentials?: {
      apiKey?: string
      userUid?: string
      serviceKey?: string
    }
  }
}
export const WizardStepContextValueDefault: WizardStepContextValueType = {
  updateAgentUid: nullFn,
  agentUid: '',
  setAgentUid: nullFn,
  appUuid: '',
  setAppUuid: nullFn,
  branches: {},
  setBranch: nullFn,
  resetBranches: nullFn,
  apiCredentials: {}
}
export const WizardStepContext =
  React.createContext<WizardStepContextValueType>(WizardStepContextValueDefault)
export const useWizardStepContextValue = ({
  organizationUuid
}): WizardStepContextValueType => {
  const [agentUid, setAgentUid] = React.useState('')
  const [appUuid, setAppUuid] = React.useState('')
  const [branches, setBranches] = React.useState({})
  const { data: orgSettings } = useOrganizationSettingsQuery({
    organizationUuid
  })
  const { data: apiKeys = {}, refetch } = useApiKeys({
    organizationUuid,
    agentUid
  })
  const { apiCredentials: userCredentials } = useApiCredentials({
    organizationUuid,
    enabled: true
  })
  const setBranch = React.useCallback(
    (key, value) => {
      setBranches(() => ({ ...branches, [key]: value }))
    },
    [branches]
  )
  const resetBranches = React.useCallback(() => setBranches({}), [])

  const updateAgentUid = (agenteUid: string) => {
    setAgentUid(agenteUid)
  }

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentUid])

  return {
    updateAgentUid,
    agentUid,
    setAgentUid,
    branches,
    setBranch,
    resetBranches,
    appUuid,
    setAppUuid,
    apiCredentials: {
      agentUrl: orgSettings?.agentUrl,
      ...userCredentials, // camelcase keys: apiKey, serviceKey, userUid
      ...apiKeys // snakecase keys: api_key, service_key, user_uid
    }
  }
}

export const AgentWizardSteps: React.FC = () => {
  const [showEditor, setShowEditor] = React.useState(false)
  const currentLanguage = getCurrentLanguage()
  const organizationUuid = getOrgUuid()
  const stepsMap = stepMappingNormalizeAgentDownload
  const steps = stepsMap[currentLanguage] || onboardViaDocsAgentWizardSteps

  return (
    <WizardStepContext.Provider
      value={useWizardStepContextValue({ organizationUuid })}
    >
      <div
        data-testid={'normalized-agent-wizard-steps'}
        onKeyUp={(event) =>
          event.ctrlKey && event.key === 'e' && setShowEditor(!showEditor)
        }
        className={css`
          display: flex;
          justify-content: center;
          width: 64vw;
          flex-direction: column;
        `}
      >
        {showEditor && (
          <AgentWizardStepContainer
            number={0}
            step={{
              defaultIsOpen: true,
              titleMsg: 'Wizard Message Editor'
            }}
          >
            <AgentWizardEditor />
          </AgentWizardStepContainer>
        )}
        <AgentWizardStepContainer
          step={{
            defaultIsOpen: true,
            titleMsg: 'title_selectALanguage'
          }}
        >
          <AgentWizardLanguageSelect
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
          />
        </AgentWizardStepContainer>
        <AgentWizardStepList steps={steps} index={2} />
      </div>
    </WizardStepContext.Provider>
  )
}
